import React, { useRef, useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useAccountStore } from '../../stores/accountStore';
import { apiService } from '../../services/api';
import { buildStaticUrl } from '../../services/supportApi';
import { PasswordInput } from '../common/PasswordInput';

interface ProfileDrawerProps {
  open: boolean;
  onClose: () => void;
}

/** 右侧抽屉：查看/编辑资料（email/nickname/bio）、改密码、上传头像。不展示电话。 */
export const ProfileDrawer: React.FC<ProfileDrawerProps> = ({ open, onClose }) => {
  const { user, getCurrentUser } = useAuthStore();
  const account = useAccountStore((s) => s.account);
  const fetchAccount = useAccountStore((s) => s.fetchAccount);
  const fileRef = useRef<HTMLInputElement>(null);

  const [nickname, setNickname] = useState(user?.nickname || '');
  const [email, setEmail] = useState(user?.email || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showSub, setShowSub] = useState(false);

  // 邀请链接：朋友点开自动填推荐码进入注册页（RegisterPage 读取 ?ref=）
  const referralLink = account?.referralCode
    ? `https://www.xmocktest.com/register?ref=${account.referralCode}`
    : '';

  const copyReferral = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setMsg('Copy failed, please copy manually');
    }
  };

  React.useEffect(() => {
    if (open) {
      setNickname(user?.nickname || '');
      setEmail(user?.email || '');
      setBio(user?.bio || '');
      setNewPassword('');
      setMsg(null);
    }
  }, [open, user]);

  if (!open) return null;

  const avatar = user?.avatar_url ? buildStaticUrl(user.avatar_url) : '';
  const initial = (user?.nickname || 'U').charAt(0).toUpperCase();

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const body: any = { nickname, email, bio };
      if (newPassword) body.password = newPassword;
      const res: any = await apiService.put('/auth/profile', body);
      if (res.success) {
        await getCurrentUser();
        setMsg('Saved');
        setNewPassword('');
      } else {
        setMsg(res.message || 'Failed to save');
      }
    } catch (e: any) {
      setMsg(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true);
    try {
      const res: any = await apiService.upload('/auth/avatar', file, 'avatar');
      if (res.success) {
        await getCurrentUser();
        setMsg('Avatar updated');
      }
    } catch (err: any) {
      setMsg(err.message || 'Failed to upload avatar');
    } finally {
      setSaving(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <>
      {/* 半透明遮罩（点击关闭） */}
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      {/* 右侧抽屉 */}
      <div className="fixed top-0 right-0 z-50 h-full w-full sm:w-96 bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 h-14 border-b border-primary-100">
          <h3 className="font-semibold text-gray-800">Profile</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* 头像 */}
          <div className="flex flex-col items-center gap-3">
            {avatar ? (
              <img src={avatar} alt="Avatar" className="w-20 h-20 rounded-full object-cover ring-2 ring-primary-200" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-2xl font-semibold ring-2 ring-primary-200">
                {initial}
              </div>
            )}
            <button onClick={() => fileRef.current?.click()} className="text-sm text-primary-600 hover:text-primary-700">
              Change avatar
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatar} />
          </div>

          {/* 账户概览 */}
          {account && (
            <div className="space-y-2">
              {/* Credits / Referrals 一行两列 */}
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="bg-primary-50 rounded-lg py-2">
                  <div className="text-lg font-semibold text-primary-700">{account.creditBalance}</div>
                  <div className="text-[11px] text-gray-500">Credits</div>
                </div>
                <div className="bg-primary-50 rounded-lg py-2">
                  <div className="text-lg font-semibold text-primary-700">{account.referralCount}</div>
                  <div className="text-[11px] text-gray-500">Referrals</div>
                </div>
              </div>

              {/* Referral code 单独一行 + 复制邀请链接 */}
              <div className="bg-primary-50 rounded-lg px-3 py-2.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[11px] text-gray-500">Referral code</div>
                    <div className="text-base font-semibold text-primary-700 tracking-wide">{account.referralCode}</div>
                  </div>
                  <button
                    type="button"
                    onClick={copyReferral}
                    disabled={!referralLink}
                    className="shrink-0 text-xs px-3 py-1.5 rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 transition"
                    title="Copy invite link"
                  >
                    {copied ? '✓ Copied' : 'Copy invite link'}
                  </button>
                </div>
                <p className="mt-1.5 text-[11px] text-gray-400 leading-snug">
                  Share the link with friends — you'll earn credit rewards when they sign up.
                </p>
              </div>

              {/* My Subscription 查询 */}
              <div className="bg-primary-50 rounded-lg px-3 py-2.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[11px] text-gray-500">My Subscription</div>
                  <button
                    type="button"
                    onClick={() => { setShowSub((v) => !v); fetchAccount(); }}
                    className="shrink-0 text-xs px-3 py-1.5 rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition"
                  >
                    {showSub ? 'Hide' : 'Check subscription'}
                  </button>
                </div>
                {showSub && (
                  <div className="mt-2 space-y-1.5">
                    {(['sat', 'ap'] as const).map((p) => {
                      const s = account.subscriptions?.[p];
                      const active = !!s?.active;
                      return (
                        <div key={p} className="flex items-center justify-between text-sm">
                          <span className="font-medium text-gray-700">{p.toUpperCase()}</span>
                          {active ? (
                            <span className="text-primary-700">
                              Active · until {s?.expiresAt ? new Date(s.expiresAt).toISOString().slice(0, 10) : '—'}
                            </span>
                          ) : (
                            <span className="text-gray-400">Not subscribed</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 表单（不含电话） */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Nickname</label>
              <input className="input" value={nickname} onChange={(e) => setNickname(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Email</label>
              <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Bio</label>
              <textarea className="input" rows={3} value={bio} onChange={(e) => setBio(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">New Password (leave blank to keep)</label>
              <PasswordInput
                autoComplete="new-password"
                placeholder="Leave blank to keep current"
                value={newPassword}
                onChange={setNewPassword}
              />
            </div>
          </div>

          {msg && <p className="text-sm text-primary-600">{msg}</p>}
        </div>

        <div className="p-5 border-t border-primary-100">
          <button onClick={handleSave} disabled={saving} className="btn btn-primary w-full">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
};
